/**
 * @module providers/SavedPageState
 * @description React Context Provider for page-level state persistence across navigation.
 * Wraps the pageStateStack module to provide React hooks for managing state that persists
 * through browser history navigation (forward/back). Automatically syncs with history changes.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  createContext,
} from 'react';
import { globalHistory } from '@reach/router';

import {
  getCurrentPageState,
  setPageState,
  ORIGIN_KEY,
} from 'modules/pageStateStack';

const PageStateContext = createContext();

// there's no state on first landing,
// but there IS a key on page reload
let currentHistoryKey = window.history?.state?.key || ORIGIN_KEY;

/**
 * @typedef BasicProps
 * @prop {string} className css className (*unused here*)
 * @prop {ReactNode} children children nodes
 */

/**
 * @component SavedPageState
 * @type {React.FC<BasicProps>}
 * @summary React Context Provider wrapper for `PageStateContext.Provider`
 *  - gets initial page history state
 *  - listens for changes, updates component/context state to match
 *  - context value is equal to current page state (`pageStateOnLoad` as it updates)
 * @param {BasicProps} props component props
 * @returns {React.Provider} React Context provider with page state
 */
const SavedPageState = ({ children }) => {
  const [pageStateOnPageLoad, setPageStateOnPageLoad] = useState(
    getCurrentPageState(),
  );

  useEffect(() => {
    globalHistory.listen(({ location, action }) => {
      const newKey = location?.state?.key || ORIGIN_KEY;

      // this helps protect against the globalListen's double firing
      // when navigate(-1) is used
      if (newKey === currentHistoryKey) return;

      currentHistoryKey = newKey;

      const currentPageState = getCurrentPageState();
      setPageStateOnPageLoad(currentPageState);
    });
  }, []);

  return (
    <PageStateContext.Provider value={pageStateOnPageLoad}>
      {children}
    </PageStateContext.Provider>
  );
};

/**
 * @callback PageStateHook
 * @param {Array<History>} pageStateOnPageLoad
 * @param {function} setSectionOpen sets page state to include given section
 */

/**
 * @func usePageState
 * @summary React Hook to wrap `PageStateContext` to get value for components inside of above provider
 * @returns {PageStateHook} tuple with page state functions
 */
export const usePageState = () => {
  const pageStateOnPageLoad = useContext(PageStateContext);

  const setSectionOpen = useCallback((sectionName, isOpen) => {
    const currentPageState = getCurrentPageState();
    setPageState(currentHistoryKey, {
      ...currentPageState,
      [sectionName]: isOpen,
    });
  }, []);

  return [pageStateOnPageLoad, setSectionOpen];
};

export default SavedPageState;
